import React, { useEffect, useState } from 'react'
import { imageUpdatesAPI, type SiteUpdates } from '@hpanel/web-common/api/client'

/**
 * Automatic security updates for one site: pin it to its current build, and
 * see its recent updates. GET/PUT /api/runtime/{container_id}/updates.
 * Errors show the server's `detail` verbatim (CLAUDE.md §9).
 */
const STATUS_TEXT: Record<string, string> = {
  updated: 'Updated',
  rolled_back: 'Update undone — the site did not work on the new build',
  rollback_failed: 'Update failed — your administrator can see this on their Security Updates page',
  pinned: 'Skipped (pinned)',
  skipped: 'Skipped',
  pending: 'Waiting',
}

export const SiteUpdatesPanel: React.FC<{ containerId: number }> = ({ containerId }) => {
  const [data, setData] = useState<SiteUpdates | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    void (async () => {
      try {
        const d = await imageUpdatesAPI.siteUpdates(containerId)
        if (!cancelled) setData(d)
      } catch (e: unknown) {
        // eslint-disable-next-line no-console
        console.error('[SiteUpdatesPanel] load failed', e)
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load security updates')
      }
    })()
    return () => { cancelled = true }
  }, [containerId])

  const onToggle = async (pinned: boolean) => {
    setBusy(true)
    setError(null)
    // Show the new state at once; put it back if the server refuses.
    setData((d) => (d ? { ...d, pinned } : d))
    try {
      setData(await imageUpdatesAPI.setPinned(containerId, pinned))
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SiteUpdatesPanel] pin change failed', e)
      setData((d) => (d ? { ...d, pinned: !pinned } : d))
      setError(e instanceof Error ? e.message : 'Failed to change the setting')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="site-updates" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security updates</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        When a fixed build of this site's image is published (same version, for example PHP 8.4.27 → 8.4.28),
        your administrator's panel moves the site to it automatically. The site restarts for a few seconds;
        if it stops working, it goes straight back to the build it had.
      </p>
      {error && <p data-testid="site-updates-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {data && (
        <>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              data-testid="site-updates-pin"
              checked={data.pinned}
              disabled={busy}
              onChange={(e) => void onToggle(e.target.checked)}
            />
            Keep this site on its current build (no automatic security updates)
          </label>
          {data.pinned && (
            <p data-testid="site-updates-pinned-warning" className="text-sm text-amber-700">
              This site will not get security fixes until you clear this box.
            </p>
          )}
          {data.history.length > 0 ? (
            <ul data-testid="site-updates-history" className="text-sm space-y-1">
              {data.history.map((h, i) => (
                <li key={i}>
                  {h.updated_at ? new Date(h.updated_at).toLocaleString() : ''} —{' '}
                  {h.from_version ?? '?'} → {h.to_version ?? '?'}: {STATUS_TEXT[h.status] ?? h.status}
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid="site-updates-none" className="text-sm text-gray-500">No security updates applied to this site yet.</p>
          )}
        </>
      )}
    </div>
  )
}
